/**
 * Recurring items (decision 0035, D4): a schedule is stored and shown, and NOTHING fires on it this release. A person's
 * Run now (RunOccurrence) creates one occurrence. Pure helpers: cron and zone checks, the occurrence's period text.
 */

const NAMES: Readonly<Record<number, ReadonlyArray<string>>> = {
  3: ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"],
  4: ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"],
}
const RANGES: ReadonlyArray<readonly [number, number]> = [
  [0, 59], // minute
  [0, 23], // hour
  [1, 31], // day of month
  [1, 12], // month
  [0, 7], // day of week (0 and 7 = Sunday)
]

const fieldValue = (field: number, v: string): number | null => {
  const names = NAMES[field]
  const upper = v.toUpperCase()
  if (names?.includes(upper)) return names.indexOf(upper) + (field === 3 ? 1 : 0)
  if (!/^\d+$/.test(v)) return null
  return Number(v)
}

const partOk = (field: number, part: string): boolean => {
  const [lo, hi] = RANGES[field] as readonly [number, number]
  const [base, step] = part.split("/")
  if (base === undefined || part.split("/").length > 2) return false
  if (step !== undefined && (!/^\d+$/.test(step) || Number(step) < 1)) return false
  if (base === "*") return true
  const [a, b] = base.split("-")
  if (a === undefined || base.split("-").length > 2) return false
  const x = fieldValue(field, a)
  if (x === null || x < lo || x > hi) return false
  if (b === undefined) return step === undefined
  const y = fieldValue(field, b)
  return y !== null && y >= x && y <= hi
}

/** Why a 5-field cron expression is not valid, or null. Minute, hour, day of month, month, day of week. */
export const cronProblem = (cron: string): string | null => {
  const fields = cron.trim().split(/\s+/)
  if (fields.length !== 5) return "a schedule is a 5-field cron: minute hour day-of-month month day-of-week"
  for (let i = 0; i < 5; i++) {
    const f = fields[i] ?? ""
    if (!f.split(",").every((p) => partOk(i, p))) return `cron field ${i + 1} ("${f}") is not valid`
  }
  return null
}

/** A parsed cron: the matching values of each field, and whether day of month / day of week start with `*`. */
interface CronSets {
  readonly minutes: ReadonlyArray<number>
  readonly hours: ReadonlyArray<number>
  readonly dom: ReadonlySet<number>
  readonly months: ReadonlySet<number>
  readonly dow: ReadonlySet<number>
  readonly domStar: boolean
  readonly dowStar: boolean
}

const expand = (field: number, text: string): ReadonlyArray<number> => {
  const [lo, hi] = RANGES[field] as readonly [number, number]
  const out = new Set<number>()
  for (const part of text.split(",")) {
    const [base = "*", stepText] = part.split("/")
    const step = stepText === undefined ? 1 : Number(stepText)
    let from = lo
    let to = hi
    if (base !== "*") {
      const [a = "", b] = base.split("-")
      from = fieldValue(field, a) ?? lo
      to = b === undefined ? (stepText === undefined ? from : hi) : (fieldValue(field, b) ?? hi)
    }
    for (let v = from; v <= to; v += step) out.add(field === 4 && v === 7 ? 0 : v)
  }
  return [...out].sort((x, y) => x - y)
}

/** The value sets of a valid 5-field cron (null when it is not valid). */
export const parseCron = (cron: string): CronSets | null => {
  if (cronProblem(cron) !== null) return null
  const f = cron.trim().split(/\s+/) as [string, string, string, string, string]
  return {
    minutes: expand(0, f[0]),
    hours: expand(1, f[1]),
    dom: new Set(expand(2, f[2])),
    months: new Set(expand(3, f[3])),
    dow: new Set(expand(4, f[4])),
    domStar: f[2].startsWith("*"),
    dowStar: f[4].startsWith("*"),
  }
}

const formatters = new Map<string, Intl.DateTimeFormat>()
const formatterOf = (tz: string): Intl.DateTimeFormat => {
  let f = formatters.get(tz)
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
    formatters.set(tz, f)
  }
  return f
}

/** The wall clock of `at` in `tz` (an unknown zone reads as UTC). */
export interface WallTime {
  readonly year: number
  readonly month: number
  readonly day: number
  readonly hour: number
  readonly minute: number
  /** 0 = Sunday. */
  readonly weekday: number
}

export const wallTimeOf = (at: number, tz: string): WallTime => {
  const parts = formatterOf(isTimeZone(tz) ? tz : "UTC").formatToParts(new Date(at))
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0)
  const year = get("year")
  const month = get("month")
  const day = get("day")
  return {
    year,
    month,
    day,
    hour: get("hour") % 24,
    minute: get("minute"),
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  }
}

/** The zone's offset at `at` in ms (local wall clock − UTC). */
const offsetAt = (at: number, tz: string): number => {
  const w = wallTimeOf(at, tz)
  const sec = Math.floor(at / 1000) % 60
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, sec) - (at - (at % 1000))
}

/**
 * The instant of a wall-clock minute in `tz`: the earlier one when the minute happens twice (autumn), null when it
 * does not happen at all (the spring gap).
 */
export const instantOfWall = (
  tz: string,
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): number | null => {
  const zone = isTimeZone(tz) ? tz : "UTC"
  const guess = Date.UTC(year, month - 1, day, hour, minute)
  const candidates = new Set<number>()
  for (const probe of [guess - 3 * 3_600_000, guess, guess + 3 * 3_600_000])
    candidates.add(guess - offsetAt(probe, zone))
  const hits = [...candidates]
    .filter((t) => {
      const w = wallTimeOf(t, zone)
      return w.year === year && w.month === month && w.day === day && w.hour === hour && w.minute === minute
    })
    .sort((a, b) => a - b)
  return hits[0] ?? null
}

/** Days looked ahead before a cron counts as never matching (Feb 29 on a Monday comes within 28 years). */
const MAX_DAYS = 366 * 29

/**
 * Wave C (decision 0071): the first minute after `after` (epoch ms) that the 5-field `cron` matches in the IANA zone
 * `tz`, DST-safe; null when the cron is not valid or never matches. The one cron parser for recurring series and
 * monitors (`monitors.ts` adds the floor and quiet hours).
 *
 * Days match as in cron: when both day of month and day of week are restricted, either one matching is enough. Each
 * wall-clock minute matches once: in the autumn hour that happens twice the first one counts, and a minute in the
 * spring gap (02:30 in Europe/Berlin on the last Sunday of March) does not happen that day.
 */
export const nextCronAt = (cron: string, tz: string, after: number): number | null => {
  const c = parseCron(cron)
  if (!c) return null
  const zone = isTimeZone(tz) ? tz : "UTC"
  const start = wallTimeOf(after, zone)
  for (let k = 0; k < MAX_DAYS; k++) {
    const d = new Date(Date.UTC(start.year, start.month - 1, start.day + k))
    const year = d.getUTCFullYear()
    const month = d.getUTCMonth() + 1
    const day = d.getUTCDate()
    if (!c.months.has(month)) continue
    const domOk = c.dom.has(day)
    const dowOk = c.dow.has(d.getUTCDay())
    const dayOk = c.domStar && c.dowStar ? true : c.domStar ? dowOk : c.dowStar ? domOk : domOk || dowOk
    if (!dayOk) continue
    for (const hour of c.hours) {
      // the start day: hours before the wall clock's hour minus one are past (one hour of slack for the autumn hour)
      if (k === 0 && hour < start.hour - 1) continue
      for (const minute of c.minutes) {
        const t = instantOfWall(zone, year, month, day, hour, minute)
        if (t !== null && t > after) return t
      }
    }
  }
  return null
}

/** Is `tz` an IANA time zone this runtime knows? */
export const isTimeZone = (tz: string): boolean => {
  if (tz.trim().length === 0) return false
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/** The calendar date of `at` in `tz`, as yyyy-mm-dd (an occurrence's `{period}`). */
export const periodOf = (at: number, tz: string): string => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: isTimeZone(tz) ? tz : "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(at))
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00"
  return `${get("year")}-${get("month")}-${get("day")}`
}

/** An occurrence's title from the series' template: `{title}` and `{period}` filled in. */
export const occurrenceTitle = (template: string, seriesTitle: string, period: string): string =>
  template.replaceAll("{title}", seriesTitle).replaceAll("{period}", period)
